import React, {useEffect, useState} from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  CircularProgress,
  Alert,
  IconButton,
  useMediaQuery,
  useTheme,
  Chip,
} from "@mui/material";
import {useTranslation} from "react-i18next";
import {
  fetchPonds,
  createPond,
  updatePond,
  deletePond,
  Pond,
  CreatePondDto,
  UpdatePondDto,
  PondType,
  WaterSource,
  getPondsWithPerformance,
} from "../../api/pond";
import {fetchFarms, Farm} from "../../api/farm";
import {Delete as DeleteIcon, Edit as EditIcon, Add as AddIcon} from "@mui/icons-material";
import {Link} from "react-router-dom";
import {formatNumber} from "../../utils/numberFormat";
import {useAuth} from "../../contexts/AuthContext";
import {UserRole} from "../../api/user";

interface PondFormData extends Omit<CreatePondDto, "farmId" | "area" | "depth"> {
  farmId: string;
  area?: number;
  depth?: number;
}

interface PondWithPerformance extends Pond {
  performanceMetrics?: any;
}

const PondListPage: React.FC = () => {
  const {t, i18n} = useTranslation();
  const {user} = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [ponds, setPonds] = useState<PondWithPerformance[]>([]);
  const [farms, setFarms] = useState<Farm[]>([]);

  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editPond, setEditPond] = useState<Pond | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<PondFormData>({
    name: "",
    type: PondType.GROWOUT,
    area: 0,
    depth: 0,
    waterSource: WaterSource.WELL,
    farmId: "",
    description: "",
  });

  const canManagePonds = Boolean(
    user &&
      [UserRole.SUPER_ADMIN, UserRole.TENANT_ADMIN, UserRole.FARMER_ADMIN].includes(user.role as UserRole)
  );

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log("Loading pond data with performance metrics...");
      const [pondsData, farmsData] = await Promise.all([getPondsWithPerformance(), fetchFarms()]);
      console.log("Ponds data with performance:", pondsData);

      // Debug: Log the first pond's performance metrics structure
      if (pondsData.length > 0 && pondsData[0].performanceMetrics) {
        console.log("First pond performance metrics structure:", pondsData[0].performanceMetrics);
        console.log("First pond performance metrics types:", {
          age: typeof pondsData[0].performanceMetrics.age,
          abw: typeof pondsData[0].performanceMetrics.abw,
          fcr: typeof pondsData[0].performanceMetrics.fcr,
          totalFeed: typeof pondsData[0].performanceMetrics.totalFeed,
          calculatedSurvival: typeof pondsData[0].performanceMetrics.calculatedSurvival,
        });
      }
      console.log("Farms data:", farmsData);

      setPonds(pondsData);
      setFarms(farmsData);

      // If there are farms but no farm is selected, select the first one
      if (farmsData.length > 0 && !formData.farmId) {
        setFormData((prev) => ({...prev, farmId: farmsData[0].id}));
      }
    } catch (err) {
      console.error("Failed to load data:", err);
      setError(err instanceof Error ? err.message : "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleOpenDialog = (pond: Pond | null = null) => {
    if (pond) {
      setEditPond(pond);
      setFormData({
        name: pond.name,
        type: pond.type,
        area: pond.area,
        depth: pond.depth,
        waterSource: pond.waterSource,
        farmId: pond.farmId,
        description: pond.description || "",
      });
    } else {
      setEditPond(null);
      setFormData({
        name: "",
        type: PondType.GROWOUT,
        area: undefined,
        depth: undefined,
        waterSource: WaterSource.WELL,
        farmId: farms[0]?.id || "",
        description: "",
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setError(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {name, value} = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const {name, value} = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    if (!formData.name || !formData.farmId) {
      setError("Name and Farm are required fields");
      return;
    }

    // Validate required numeric fields for create operation
    if (!editPond) {
      if (!formData.area || formData.area <= 0) {
        setError("Area must be a positive number");
        return;
      }
      if (!formData.depth || formData.depth <= 0) {
        setError("Depth must be a positive number");
        return;
      }
    }

    try {
      setLoading(true);
      if (editPond) {
        // Build update data, only including fields with actual values
        const updateData: UpdatePondDto = {};

        // Only add fields that have actual values
        if (formData.name) updateData.name = formData.name;
        if (formData.type) updateData.type = formData.type;
        if (formData.area && formData.area > 0) updateData.area = Number(formData.area);
        if (formData.depth && formData.depth > 0) updateData.depth = Number(formData.depth);
        if (formData.waterSource) updateData.waterSource = formData.waterSource;

        if (formData.farmId) updateData.farmId = formData.farmId;
        if (formData.description) updateData.description = formData.description;

        await updatePond(editPond.id, updateData);
      } else {
        // For create, ensure required fields are present and positive
        const createData: CreatePondDto = {
          name: formData.name,
          type: formData.type,
          area: Number(formData.area),
          depth: Number(formData.depth),
          waterSource: formData.waterSource,
          farmId: formData.farmId,
        };

        // Add optional fields only if they have values
        if (formData.description) createData.description = formData.description;

        await createPond(createData);
      }
      handleCloseDialog();
      await loadData();
    } catch (err) {
      console.error("Failed to save pond:", err);
      setError(err instanceof Error ? err.message : "Failed to save pond");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this pond?")) {
      return;
    }

    try {
      setLoading(true);
      await deletePond(id);
      await loadData();
    } catch (err) {
      console.error("Failed to delete pond:", err);
      setError(err instanceof Error ? err.message : "Failed to delete pond");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format date
  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleDateString();
  };

  // Helper function to format biomass range
  const formatBiomassRange = (biomassRange: any) => {
    if (!biomassRange) return "-";
    return `${biomassRange.min}-${biomassRange.max}`;
  };

  // Helper function to format survival range
  const formatSurvivalRange = (survivalRange: any) => {
    if (!survivalRange) return "-";
    return `${survivalRange.min}-${survivalRange.max}`;
  };

  // Helper function to get current cycle data
  const getCurrentCycleData = (pond: PondWithPerformance) => {
    if (!pond.performanceMetrics) return null;

    const metrics = pond.performanceMetrics;

    // Handle case where performance metrics has an error
    if (metrics.error) return null;

    // Get current cycle data
    const currentCycle = metrics.currentCycle || {};

    return {
      age: metrics.age || 0,
      startedAt: currentCycle.startedAt || metrics.startedAt,
      totalSeed: currentCycle.totalSeed || metrics.totalSeed || 0,
      abw: metrics.abw || 0,
      biomassRange: metrics.biomassRange,
      totalFeed: metrics.totalFeed || 0,
      fcr: metrics.fcr || 0,
      survivalRange: metrics.survivalRange,
      calculatedSurvival: metrics.calculatedSurvival,
      currentBiomass: metrics.metricsSource === "harvest"
        ? (metrics.totalBiomassProduced || metrics.calculatedBiomass || metrics.currentBiomass)
        : (metrics.calculatedBiomass || metrics.currentBiomass),
      metricsSource: metrics.metricsSource || "estimate",
    };
  };

  if (loading && ponds.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={{xs: 2, sm: 3}}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexDirection={{xs: "column", sm: "row"}} gap={{xs: 2, sm: 0}}>
        <Typography variant="h5">{t("Pond Performance")}</Typography>
        {canManagePonds && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            disabled={loading}
            fullWidth={isMobile}>
            {t("Add Pond")}
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{mb: 2}} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress />
          <Typography variant="body2" sx={{ml: 2}}>
            Loading performance data...
          </Typography>
        </Box>
      )}

      {isMobile ? (
        <Box display="flex" flexDirection="column" gap={2}>
          {ponds.map((pond) => {
            const cycleData = getCurrentCycleData(pond);
            return (
              <Paper key={pond.id} sx={{p: 2}}>
                <Typography variant="h6" gutterBottom>
                  {pond.name} - {farms.find((f) => f.id === pond.farmId)?.name || "Unknown Farm"}
                </Typography>
                <Typography variant="body2">
                  {t("Type")}: {pond.type}
                </Typography>
                <Typography variant="body2">
                  {t("Acres")}:{" "}
                  {typeof pond.area === "number"
                    ? pond.area.toFixed(2)
                    : typeof pond.area === "string"
                    ? parseFloat(pond.area).toFixed(2)
                    : pond.area}{" "}
                  acres
                </Typography>
                <Typography variant="body2">
                  {t("Farm")}: {farms.find((f) => f.id === pond.farmId)?.name || "-"}
                </Typography>
                {cycleData && (
                  <>
                    <Typography variant="body2">
                      {t("Age")}: {cycleData.age} days
                    </Typography>
                    <Typography variant="body2">
                      {t("Started")}: {formatDate(cycleData.startedAt)}
                    </Typography>
                    <Typography variant="body2">
                      {t("Stocking")}: {cycleData.totalSeed ? parseInt(cycleData.totalSeed).toLocaleString() : "-"}
                    </Typography>
                    <Typography variant="body2">
                      {t("ABW")}: {cycleData.abw ? `${parseFloat(cycleData.abw).toFixed(2)} g` : "-"}
                    </Typography>
                    <Typography variant="body2">
                      {t("Biomass")}: {cycleData.currentBiomass ? `${parseFloat(cycleData.currentBiomass).toFixed(2)} kg` : "-"}
                      {cycleData.metricsSource === "harvest" ? ` (${t("Harvest")})` : ""}
                    </Typography>
                    <Typography variant="body2">
                      {t("Feed")}: {cycleData.totalFeed ? `${parseFloat(cycleData.totalFeed).toFixed(2)} kg` : "-"}
                    </Typography>
                    <Typography variant="body2">
                      {t("FCR")}: {cycleData.fcr ? parseFloat(cycleData.fcr).toFixed(2) : "-"}
                      {cycleData.metricsSource === "harvest" ? ` (${t("Harvest")})` : ""}
                    </Typography>
                    <Typography variant="body2">
                      {t("Survival")}: {cycleData.calculatedSurvival ? `${formatNumber(parseFloat(cycleData.calculatedSurvival), "en-IN")} nos` : "-"}
                    </Typography>
                  </>
                )}
                <Box mt={1} display="flex" gap={1} flexWrap="wrap">
                  <Button size="small" variant="outlined" component={Link} to={`/ponds/${pond.id}`}>
                    {t("Details")}
                  </Button>
                  {canManagePonds && (
                    <>
                      <IconButton size="small" onClick={() => handleOpenDialog(pond)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(pond.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
                </Box>
              </Paper>
            );
          })}
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{overflowX: "auto"}}>
          <Table>
            <TableHead>
              <TableRow sx={{backgroundColor: theme.palette.primary.main}}>
                <TableCell sx={{color: "white", fontWeight: "bold"}}>{t("FARM")}</TableCell>
                <TableCell sx={{color: "white", fontWeight: "bold"}}>{t("POND")}</TableCell>
                <TableCell sx={{color: "white", fontWeight: "bold"}}>{t("ACRES")}</TableCell>
                <TableCell sx={{color: "white", fontWeight: "bold"}}>{t("AGE (d)")}</TableCell>
                <TableCell sx={{color: "white", fontWeight: "bold"}}>{t("STARTED")}</TableCell>
                <TableCell sx={{color: "white", fontWeight: "bold"}}>{t("STOCKING")}</TableCell>
                <TableCell sx={{color: "white", fontWeight: "bold"}}>{t("ABW (g)")}</TableCell>
                <TableCell sx={{color: "white", fontWeight: "bold"}}>{t("BIOMASS (kg)")}</TableCell>
                <TableCell sx={{color: "white", fontWeight: "bold"}}>{t("FEED (kg)")}</TableCell>
                <TableCell sx={{color: "white", fontWeight: "bold"}}>{t("FCR")}</TableCell>
                <TableCell sx={{color: "white", fontWeight: "bold"}}>{t("SURVIVAL (Nos)")}</TableCell>
                <TableCell align="right" sx={{color: "white", fontWeight: "bold"}}>
                  {t("Actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ponds.map((pond, index) => {
                const cycleData = getCurrentCycleData(pond);
                return (
                  <TableRow
                    key={pond.id}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "background.paper" : "action.hover",
                      "&:hover": {
                        backgroundColor: "action.selected",
                      },
                    }}>
                    <TableCell sx={{fontWeight: "medium"}}>{farms.find((f) => f.id === pond.farmId)?.name || "-"}</TableCell>
                    <TableCell>
                      <Button component={Link} to={`/ponds/${pond.id}`} sx={{textTransform: "none", fontWeight: "bold"}}>
                        {pond.name}
                      </Button>
                    </TableCell>
                    <TableCell align="right">
                      {typeof pond.area === "number"
                        ? pond.area.toFixed(2)
                        : typeof pond.area === "string"
                        ? parseFloat(pond.area).toFixed(2)
                        : pond.area}
                    </TableCell>
                    <TableCell align="right">{cycleData ? cycleData.age : "-"}</TableCell>
                    <TableCell>{cycleData ? formatDate(cycleData.startedAt) : "-"}</TableCell>
                    <TableCell align="right">{cycleData?.totalSeed ? parseInt(cycleData.totalSeed).toLocaleString() : "-"}</TableCell>
                    <TableCell align="right">{cycleData?.abw ? `${parseFloat(cycleData.abw).toFixed(2)}` : "-"}</TableCell>
                    <TableCell align="right">
                      {cycleData?.currentBiomass ? `${parseFloat(cycleData.currentBiomass).toFixed(2)}` : "-"}
                      {cycleData?.metricsSource === "harvest" ? ` (${t("Harvest")})` : ""}
                    </TableCell>
                    <TableCell align="right">{cycleData?.totalFeed ? `${parseFloat(cycleData.totalFeed).toFixed(2)}` : "-"}</TableCell>
                    <TableCell align="right">{cycleData?.fcr ? `${parseFloat(cycleData.fcr).toFixed(2)}` : "-"}</TableCell>
                    <TableCell align="right">
                      {cycleData?.calculatedSurvival ? formatNumber(parseFloat(cycleData.calculatedSurvival), i18n.language) : "-"}
                    </TableCell>
                    <TableCell align="right">
                      {canManagePonds && (
                        <>
                          <IconButton size="small" onClick={() => handleOpenDialog(pond)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(pond.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editPond ? t("Edit Pond") : t("Add New Pond")}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{mb: 2}} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <TextField
            autoFocus
            margin="normal"
            name="name"
            label={t("Name")}
            fullWidth
            value={formData.name}
            onChange={handleInputChange}
            required
            disabled={loading}
          />

          <FormControl fullWidth margin="normal" required>
            <InputLabel id="type-select-label">{t("Pond Type")}</InputLabel>
            <Select
              labelId="type-select-label"
              id="type-select"
              name="type"
              value={formData.type}
              label={t("Pond Type")}
              onChange={handleSelectChange}
              disabled={loading}
              required>
              <MenuItem value={PondType.GROWOUT}>{t("Growout")}</MenuItem>
              <MenuItem value={PondType.NURSERY}>{t("Nursery")}</MenuItem>
              <MenuItem value={PondType.BREEDING}>{t("Breeding")}</MenuItem>
              <MenuItem value={PondType.QUARANTINE}>{t("Quarantine")}</MenuItem>
              <MenuItem value={PondType.OTHER}>{t("Other")}</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal" required>
            <InputLabel id="farm-select-label">{t("Farm")}</InputLabel>
            <Select
              labelId="farm-select-label"
              id="farm-select"
              name="farmId"
              value={formData.farmId}
              label={t("Farm")}
              onChange={handleSelectChange}
              disabled={loading || farms.length === 0}
              required>
              {farms.map((farm) => (
                <MenuItem key={farm.id} value={farm.id}>
                  {farm.name}
                </MenuItem>
              ))}
              {farms.length === 0 && <MenuItem disabled>{t("No farms available")}</MenuItem>}
            </Select>
          </FormControl>

          <TextField
            margin="normal"
            name="area"
            label={t("Area (acres)")}
            type="number"
            fullWidth
            value={formData.area || ""}
            onChange={(e) => setFormData({...formData, area: e.target.value ? Number(e.target.value) : undefined})}
            inputProps={{min: 0, step: 0.01}}
            disabled={loading}
            required
          />

          <TextField
            margin="normal"
            name="depth"
            label={t("Depth (m)")}
            type="number"
            fullWidth
            value={formData.depth || ""}
            onChange={(e) => setFormData({...formData, depth: e.target.value ? Number(e.target.value) : undefined})}
            inputProps={{min: 0, step: 0.1}}
            disabled={loading}
            required
          />

          <FormControl fullWidth margin="normal">
            <InputLabel id="water-source-select-label">{t("Water Source")}</InputLabel>
            <Select
              labelId="water-source-select-label"
              id="water-source-select"
              name="waterSource"
              value={formData.waterSource}
              label={t("Water Source")}
              onChange={handleSelectChange}
              disabled={loading}>
              <MenuItem value={WaterSource.WELL}>{t("Well")}</MenuItem>
              <MenuItem value={WaterSource.RIVER}>{t("River")}</MenuItem>
              <MenuItem value={WaterSource.LAKE}>{t("Lake")}</MenuItem>
              <MenuItem value={WaterSource.RAINWATER}>{t("Rainwater")}</MenuItem>
              <MenuItem value={WaterSource.MUNICIPAL}>{t("Municipal")}</MenuItem>
              <MenuItem value={WaterSource.OTHER}>{t("Other")}</MenuItem>
            </Select>
          </FormControl>

          <TextField
            margin="normal"
            name="description"
            label={t("Description")}
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={handleInputChange}
            disabled={loading}
          />
        </DialogContent>
        <DialogActions sx={{p: 2}}>
          <Button onClick={handleCloseDialog} color="inherit" disabled={loading}>
            {t("Cancel")}
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={
              loading ||
              !formData.name ||
              !formData.farmId ||
              (!editPond && (!formData.area || formData.area <= 0 || !formData.depth || formData.depth <= 0))
            }>
            {loading ? <CircularProgress size={24} /> : t("Save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PondListPage;
